import { useEffect, useState } from 'react';
import { message } from 'antd';
import { gql } from 'graphql-tag';
import { useApolloClient } from '@apollo/client';
import {
	REVERSE_MAPING_BADGE_TYPE,
	ICON_COMPLETE_MAP,
	BADGE_TYPE,
} from '../../RnRCommonComponent/CoreBadge/rewardsConstants.js';

const QUERY_BADGE_ASSIGNMENTS_BY_USER_ID_POSTGRES = gql`
	query QueryBadgeAssignmentsByUserIdPostgres(
		$userId: ID!
		$limit: Int
		$nextToken: String
	) {
		queryBadgeAssignmentsByUserIdPostgres(
			userId: $userId
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				id
				accountClaimId
				accountClaim {
					id
					firstName
					lastName
				}
				badgeId
				userId
				awardedBy
				awardedByUser {
					id
					firstName
					lastName
				}
				approvalStatus
				quietDecline
				rewardBonus
				points
				createdAt
				updatedAt
				completedAt
				sentWithoutSnappyGrant
				justificationFormAnswers
				certificateKey
				content {
					message
					gifURL
					tags
				}
				badge {
					id
					name
					label
					description
					image
					color
					gradientColor
					points
					bonusPoints
					rewardBonus
					iconId
					companyId
					icon {
						id
						name
						icon
						iconType
						code
					}
					badgeTypeId
					badgeType {
						id
						name
						code
						shape {
							id
							name
							shape
						}
					}
					snappyGrantId
					snappyGrant {
						id
						name
						costInCurrency
						externalSnappyId
						snappyCampaignTierId
					}
					isAssignmentRestricted
					shape
					badgeRotation
					customImageKey
					customImageSignedUrl
					labelColor
					customIconKey
					customIconSignedUrl
					category
					milestoneRewardId
					milestoneReward {
						id
						name
						image
					}
					customShareImages {
						field
						prefix
						url
					}
				}
				user {
					id
					firstName
					lastName
					emailAddress
				}
			}
			nextToken
		}
	}
`;

const QUERY_MY_BADGE_ASSIGNMENTS = gql`
	query QueryMyBadgeAssignments(
		$companyId: ID!
		$userId: ID
		$awardedBy: ID
		$accountClaimId: ID
		$approvalStatus: ApprovalStatus
		$limit: Int
		$nextToken: String
	) {
		queryBadgeAssignments(
			companyId: $companyId
			userId: $userId
			awardedBy: $awardedBy
			accountClaimId: $accountClaimId
			approvalStatus: $approvalStatus
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				id
				accountClaimId
				accountClaim {
					id
					firstName
					lastName
				}
				badgeId
				userId
				awardedBy
				awardedByUser {
					id
					firstName
					lastName
				}
				approvalStatus
				quietDecline
				rewardBonus
				points
				createdAt
				updatedAt
				completedAt
				sentWithoutSnappyGrant
				justificationFormAnswers
				certificateKey
				content {
					message
					gifURL
					tags
				}
				badge {
					id
					name
					label
					description
					image
					color
					gradientColor
					points
					bonusPoints
					rewardBonus
					iconId
					companyId
					icon {
						id
						name
						icon
						iconType
						code
					}
					badgeTypeId
					badgeType {
						id
						name
						code
						shape {
							id
							name
							shape
						}
					}
					snappyGrantId
					snappyGrant {
						id
						name
						costInCurrency
						externalSnappyId
						snappyCampaignTierId
					}
					isAssignmentRestricted
					shape
					badgeRotation
					customImageKey
					customImageSignedUrl
					labelColor
					customIconKey
					customIconSignedUrl
					category
					milestoneRewardId
					milestoneReward {
						id
						name
						image
					}
					customShareImages {
						field
						prefix
						url
					}
				}
				user {
					id
					firstName
					lastName
					emailAddress
				}
			}
			nextToken
		}
	}
`;

const useMyBadgeAssignments = (
	currentUser,
	shouldRefresh,
	setShouldRefresh
) => {
	const [isLoading, setIsLoading] = useState(false);
	const [badgeList, setBadgeList] = useState([]);
	const [assignmentsSent, setAssignmentsSent] = useState([]);
	const [hasMore, setHasMore] = useState(false);
	const apolloClient = useApolloClient();

	const fetchMyBadgeAssignments = async () => {
		if (!currentUser?.id || !currentUser?.company?.id) {
			return;
		}

		setIsLoading(true);
		try {
			let allAssignments = [];
			let awardedByAssignments = [];

			// First query: Get assignments by userId (automatically includes accountClaimId)
			let nextToken = null;
			do {
				const { data, error } = await apolloClient.query({
					query: QUERY_BADGE_ASSIGNMENTS_BY_USER_ID_POSTGRES,
					variables: {
						userId: currentUser.id,
						limit: 1000,
						...(nextToken && { nextToken }),
					},
					fetchPolicy: 'network-only',
				});

				if (error) {
					console.error('Error fetching my badge assignments:', error);
					message.error('Failed to fetch your recognitions! Try again later.');
					return;
				}

				if (data?.queryBadgeAssignmentsByUserIdPostgres?.items) {
					allAssignments = [
						...allAssignments,
						...data.queryBadgeAssignmentsByUserIdPostgres.items,
					];

					nextToken = data.queryBadgeAssignmentsByUserIdPostgres.nextToken;
				} else {
					break;
				}
			} while (nextToken);

			let awardedByNextToken = null;
			do {
				const { data, error } = await apolloClient.query({
					query: QUERY_MY_BADGE_ASSIGNMENTS,
					variables: {
						companyId: currentUser.company.id,
						awardedBy: currentUser.id,
						approvalStatus: 'Approved',
						limit: 1000,
						...(awardedByNextToken && {
							nextToken: awardedByNextToken,
						}),
					},
					fetchPolicy: 'network-only',
				});

				if (error) {
					console.error('Error fetching sent badge assignments:', error);
					break;
				}

				if (data?.queryBadgeAssignments?.items) {
					awardedByAssignments = [
						...awardedByAssignments,
						...data.queryBadgeAssignments.items,
					];

					awardedByNextToken = data.queryBadgeAssignments.nextToken;
				} else {
					break;
				}
			} while (awardedByNextToken);

			// Dedupe assignments by assignment ID
			const uniqueAssignments = allAssignments.reduce((acc, assignment) => {
				if (!acc.find((existing) => existing.id === assignment.id)) {
					acc.push(assignment);
				}
				return acc;
			}, []);

			const transformedBadges = uniqueAssignments
				.filter(
					(assignment) =>
						(assignment.badge?.companyId === currentUser.company.id ||
							!assignment.badge?.companyId) &&
						assignment.badgeId
				)
				.map((assignment) => {
					const badge = assignment.badge;

					const badgeTypeCode =
						REVERSE_MAPING_BADGE_TYPE[badge?.badgeTypeId] || '';
					const badgeType = BADGE_TYPE[badgeTypeCode];

					let iconInfo = null;
					if (badge?.icon) {
						iconInfo = badge.icon;
					} else if (badge?.iconId) {
						iconInfo = ICON_COMPLETE_MAP.find(
							(icon) => icon.id === badge.iconId
						);
					}

					return {
						// Badge assignment fields
						assignmentId: assignment.id,
						badgeId: assignment.badgeId,
						userId: assignment.userId,
						awardedBy: assignment.awardedBy,
						approvalStatus: assignment.approvalStatus,
						rewardBonus: assignment.rewardBonus,
						points: assignment.points || badge?.points,
						createdAt: assignment.createdAt,
						updatedAt: assignment.updatedAt,
						completedAt: assignment.completedAt,
						justificationFormAnswers: assignment.justificationFormAnswers,
						awardedByUser: assignment.awardedByUser,
						sentWithoutSnappyGrant: assignment.sentWithoutSnappyGrant,

						// Badge fields for display
						id: badge?.id,
						name: badge?.name,
						label: badge?.label,
						description: badge?.description,
						image: badge?.image,
						color: badge?.color,
						gradientColor: badge?.gradientColor,
						bonusPoints: badge?.bonusPoints,
						iconId: badge?.iconId,
						badgeTypeId: badge?.badgeTypeId,
						snappyGrantId: badge?.snappyGrantId,
						snappyGrant: badge?.snappyGrant,
						shape: badge?.shape,
						badgeRotation: badge?.badgeRotation,
						customImageSignedUrl: badge?.customImageSignedUrl,
						labelColor: badge?.labelColor,
						customIconSignedUrl: badge?.customIconSignedUrl,
						milestoneRewardId: badge?.milestoneRewardId,
						milestoneReward: badge?.milestoneReward,

						// Essential fields for proper badge rendering
						badgeType: badgeType,
						iconInfo: iconInfo,

						// Additional fields for compatibility
						issuedDate: assignment.completedAt || assignment.updatedAt,
						issued: 1, // For display purposes - shows "1 Issued" under badge
						recipient: assignment.user,

						// For clicking on badges
						badge: {
							...badge,
							completedAt: assignment.completedAt,
							issuedDate: assignment.completedAt || assignment.updatedAt,
							badgeType: badgeType,
							iconInfo: iconInfo,
						},
						rawAssignment: assignment,
					};
				});

			setBadgeList(transformedBadges);
			setAssignmentsSent(awardedByAssignments);
			setShouldRefresh && setShouldRefresh(false);

			// Cache the results
			localStorage.setItem(
				'myBadgeAssignments',
				JSON.stringify(transformedBadges)
			);
		} catch (error) {
			console.error('Exception when fetching my badge assignments:', error);
			message.error('Failed to fetch your recognitions! Try again later.');
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (shouldRefresh) {
			fetchMyBadgeAssignments();
		}
	}, [shouldRefresh]);

	useEffect(() => {
		fetchMyBadgeAssignments();
	}, [currentUser?.id, currentUser?.company?.id]);

	const fetchMoreData = () => {
		fetchMyBadgeAssignments();
	};

	// Try to get cached data if available
	const cachedBadgeList = JSON.parse(
		localStorage.getItem('myBadgeAssignments') || '[]'
	);

	return {
		isLoading,
		badgeList: badgeList.length > 0 ? badgeList : cachedBadgeList,
		assignmentsSent: assignmentsSent,
		hasMore,
		fetchMoreData,
	};
};

export default useMyBadgeAssignments;
